import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeading from '../common/SectionHeading';

const SectionContainer = styled.section`
  background-color: #121212;
  color: #FFFFFF;
  padding: 100px 0;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const CarouselContainer = styled.div`
  position: relative;
  margin: 60px auto;
  overflow: hidden;
  touch-action: pan-y;
  user-select: none;
  max-width: 1200px;
  
  @media (max-width: 1024px) {
    max-width: 400px;
    border-radius: 24px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    background-color: #000;
  }
  
  @media (max-width: 768px) {
    max-width: 300px;
    border-radius: 20px;
    margin: 40px auto;
  }
  
  @media (max-width: 480px) {
    max-width: 280px;
  }
`;

const DesktopCarouselWrapper = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: flex-start;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const MobileCarouselWrapper = styled(motion.div)`
  display: none;
  position: relative;
  width: 100%;
  
  @media (max-width: 1024px) {
    display: block;
  }
`;

const ScreenshotWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 866px;
  aspect-ratio: 1125/2436;
  
  @media (max-width: 768px) {
    height: 650px;
  }
  
  @media (max-width: 480px) {
    height: 606px;
  }
`;

const DesktopScreenshot = styled(motion.div)`
  flex: 0 0 auto;
  width: 300px;
  height: 650px;
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  background-color: #000;
  
  &.center {
    width: 350px;
    height: 760px;
    z-index: 2;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4);
  }
  
  &.side {
    opacity: 0.7;
    transform: scale(0.9);
  }
  
  @media (max-width: 1200px) {
    width: 280px;
    height: 607px;
    
    &.center {
      width: 320px;
      height: 693px;
    }
  }
`;

const Screenshot = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 24px;
  background-color: #000;
  
  @media (max-width: 768px) {
    border-radius: 20px;
  }
`;

const DesktopScreenshotImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 24px;
  background-color: #000;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(33, 150, 243, 0.9);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  color: white;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  display: none;
  
  &:hover {
    background-color: #2196F3;
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: -70px;
  }
  
  &.next {
    right: -70px;
  }
  
  @media (max-width: 1024px) {
    display: block;
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
    font-size: 16px;
    
    &.prev {
      left: -60px;
    }
    
    &.next {
      right: -60px;
    }
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    font-size: 14px;
    
    &.prev {
      left: -50px;
    }
    
    &.next {
      right: -50px;
    }
  }
`;

const DesktopNavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(33, 150, 243, 0.9);
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  color: white;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 15;
  display: none;
  
  &:hover {
    background-color: #2196F3;
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: -100px;
  }
  
  &.next {
    right: -100px;
  }
  
  @media (min-width: 1025px) {
    display: block;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 30px;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background-color: ${props => props.active ? '#2196F3' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#2196F3' : 'rgba(255, 255, 255, 0.5)'};
    transform: scale(1.2);
  }
`;

const ScreenshotTitle = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
  color: white;
  padding: 50px 20px 25px;
  border-radius: 0 0 24px 24px;
  
  h3 {
    font-size: 18px;
    margin-bottom: 6px;
    font-weight: 600;
    text-align: center;
  }
  
  p {
    font-size: 13px;
    opacity: 0.9;
    line-height: 1.4;
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: 40px 15px 20px;
    border-radius: 0 0 20px 20px;
    
    h3 {
      font-size: 16px;
    }
    
    p {
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 35px 12px 18px;
    
    h3 {
      font-size: 14px;
      margin-bottom: 4px;
    }
    
    p {
      font-size: 11px;
    }
  }
`;

const ScreenshotsCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const screenshots = [
    {
      src: '/screenshots/screen_1.PNG',
      title: 'AI Chat Interface',
      description: 'Get personalized car recommendations through our intelligent chat interface'
    },
    {
      src: '/screenshots/screen 2.PNG',
      title: 'Vehicle Analysis',
      description: 'Comprehensive analysis of vehicle features, pricing, and reliability'
    },
    {
      src: '/screenshots/screen 3.PNG',
      title: 'Comparison Reports',
      description: 'Side-by-side comparisons to help you make informed decisions'
    },
    {
      src: '/screenshots/screen 4.PNG',
      title: 'Dealership Insights',
      description: 'Independent reviews and ratings of local dealerships'
    },
    {
      src: '/screenshots/screen 5.PNG',
      title: 'Price Negotiation',
      description: 'Smart negotiation strategies based on market data'
    },
    {
      src: '/screenshots/screen 6.PNG',
      title: 'Total Cost Analysis',
      description: 'Complete ownership cost breakdown including insurance and maintenance'
    }
  ];

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => 
      prevIndex === screenshots.length - 1 ? 0 : prevIndex + 1
    );
  }, [screenshots.length]);

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? screenshots.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Handle swipe gestures
  const handleDragEnd = (event, info) => {
    setIsDragging(false);
    const threshold = 50; // Minimum distance for a swipe
    
    if (Math.abs(info.offset.x) > threshold) {
      if (info.offset.x > 0) {
        // Swiped right - go to previous
        prevSlide();
      } else {
        // Swiped left - go to next
        nextSlide();
      }
    }
  };

  const handleDragStart = () => {
    setIsDragging(true);
    setIsAutoPlaying(false);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || isDragging) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlaying, isDragging, nextSlide]);

  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => {
    if (!isDragging) {
      setIsAutoPlaying(true);
    }
  };

  // Get visible screenshots for desktop (3 images: prev, current, next)
  const getVisibleScreenshots = () => {
    const totalImages = screenshots.length;
    const prevIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
    const nextIndex = currentIndex === totalImages - 1 ? 0 : currentIndex + 1;
    
    return [
      { ...screenshots[prevIndex], position: 'prev', index: prevIndex },
      { ...screenshots[currentIndex], position: 'center', index: currentIndex },
      { ...screenshots[nextIndex], position: 'next', index: nextIndex }
    ];
  };

  const visibleScreenshots = getVisibleScreenshots();

  return (
    <SectionContainer>
      <Content>
        <SectionHeading 
          title="See CarConsul in Action" 
          subtitle="Explore how our AI helps you make smarter car buying decisions"
        />
        
        <CarouselContainer 
          ref={containerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Desktop Layout - Multiple Images */}
          <DesktopCarouselWrapper>
            {visibleScreenshots.map((screenshot, index) => (
              <DesktopScreenshot
                key={`${screenshot.index}-${screenshot.position}`}
                className={screenshot.position === 'center' ? 'center' : 'side'}
                initial={{ opacity: 0, y: 20 }}
                animate={{ 
                  opacity: screenshot.position === 'center' ? 1 : 0.7,
                  y: 0,
                  scale: screenshot.position === 'center' ? 1 : 0.9
                }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                onClick={() => {
                  if (screenshot.position !== 'center') {
                    setCurrentIndex(screenshot.index);
                  }
                }}
                style={{ 
                  cursor: screenshot.position !== 'center' ? 'pointer' : 'default'
                }}
              >
                <DesktopScreenshotImage 
                  src={screenshot.src}
                  alt={screenshot.title}
                  onError={(e) => {
                    console.log('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                />
                {screenshot.position === 'center' && (
                  <ScreenshotTitle>
                    <h3>{screenshot.title}</h3>
                    <p>{screenshot.description}</p>
                  </ScreenshotTitle>
                )}
              </DesktopScreenshot>
            ))}
            
            <DesktopNavigationButton 
              className="prev" 
              onClick={prevSlide}
              aria-label="Previous screenshot"
            >
              ‹
            </DesktopNavigationButton>
            
            <DesktopNavigationButton 
              className="next" 
              onClick={nextSlide}
              aria-label="Next screenshot"
            >
              ›
            </DesktopNavigationButton>
          </DesktopCarouselWrapper>

          {/* Mobile Layout - Single Image */}
          <MobileCarouselWrapper>
            <AnimatePresence mode="wait">
              <ScreenshotWrapper
                key={currentIndex}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
              >
                <Screenshot 
                  src={screenshots[currentIndex].src}
                  alt={screenshots[currentIndex].title}
                  onError={(e) => {
                    console.log('Image failed to load:', e.target.src);
                    e.target.style.display = 'none';
                  }}
                />
                <ScreenshotTitle>
                  <h3>{screenshots[currentIndex].title}</h3>
                  <p>{screenshots[currentIndex].description}</p>
                </ScreenshotTitle>
              </ScreenshotWrapper>
            </AnimatePresence>
            
            <NavigationButton 
              className="prev" 
              onClick={prevSlide}
              aria-label="Previous screenshot"
            >
              ‹
            </NavigationButton>
            
            <NavigationButton 
              className="next" 
              onClick={nextSlide}
              aria-label="Next screenshot"
            >
              ›
            </NavigationButton>
          </MobileCarouselWrapper>
        </CarouselContainer>
        
        <DotsContainer>
          {screenshots.map((_, index) => (
            <Dot
              key={index}
              active={index === currentIndex}
              onClick={() => goToSlide(index)}
              aria-label={`Go to screenshot ${index + 1}`}
            />
          ))}
        </DotsContainer>
      </Content>
    </SectionContainer>
  );
};

export default ScreenshotsCarousel;